/**
 * Client-side Hooks
 *
 * Runs only in the browser (not during SSR)
 * Initializes authentication and Sentry when the app loads
 */

import { initAuth } from '$features/auth';
import { loadConfig } from '$config';
import * as Sentry from '@sentry/sveltekit';
import { handleErrorWithSentry, replayIntegration } from '@sentry/sveltekit';
import type { HandleClientError } from '@sveltejs/kit';

// Initialize Sentry with runtime config
let sentryInitialized = false;

async function initializeSentry() {
  if (sentryInitialized) return;

  try {
    const config = await loadConfig();

    // Skip initialization if Sentry is disabled
    if (!config.sentry.enabled) {
      console.log('Sentry disabled in this environment');
      return;
    }

    Sentry.init({
      // DSN from runtime config
      dsn: config.sentry.dsn,

      // Environment from runtime config
      environment: config.sentry.environment,

      // Performance Monitoring
      tracesSampleRate: config.sentry.tracesSampleRate,

      // Trace propagation for distributed tracing
      tracePropagationTargets: ['localhost', /^https:\/\/.*\.stathealth\.io/, config.api.host],

      // Session Replay
      replaysSessionSampleRate: config.sentry.replaysSessionSampleRate,
      replaysOnErrorSampleRate: config.sentry.replaysOnErrorSampleRate,

      // Integrations
      integrations: [
        replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],

      // Release tracking (from build metadata)
      release: `web-app@${__APP_VERSION__}`,

      // Only enable in production mode
      enabled: import.meta.env.MODE === 'production',

      // Custom before send hook (for PII filtering if needed)
      beforeSend(event, _hint) {
        // Additional filtering can be added here if needed
        return event;
      },
    });

    sentryInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Sentry:', error);
  }
}

/**
 * Handle unexpected client errors
 */
export const handleError: HandleClientError = handleErrorWithSentry(({ error, event: _event }) => {
  console.error('Client error:', error);

  // Return user-friendly message
  return {
    message: 'An unexpected error occurred. Please try refreshing the page.',
  };
});

// Initialize app on mount
if (typeof window !== 'undefined') {
  // The app is light only now; drop the preference the old dark-mode toggle saved.
  try {
    localStorage.removeItem('theme');
  } catch {
    // Storage can be unavailable (private mode, blocked site data); there is nothing to clear then.
  }

  // Initialize Sentry first, then load config and auth
  initializeSentry()
    .then(() => loadConfig())
    .then(() => {
      // Then initialize auth
      return initAuth();
    })
    .catch((error) => {
      console.error('Failed to initialize app:', error);
      Sentry.captureException(error);
    });
}
